import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, Users, Star, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { SCREENS } from '../../navigation/navConfig';
import { useDroits, hasDroit, rubriqueOfScreen, invalidateDroits, estDirecteur } from '../../lib/droits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES_GROUPES = ['Secrétaire', 'Assistant', 'Médecin'];
// Rubriques affichées dans le même écran sans lien parent/enfant
const RUBRIQUES_LIEES = { planning: ['agenda'] };
const roleKey = (r) => (r === 'Assistant(e)' ? 'Assistant' : r);

/** Sonde SCREENS[id].allow pour retrouver la rubrique quand le mapping n'existe pas. */
const probeRubriques = (screenId) => {
  const s = SCREENS[screenId];
  if (!s?.allow) return [];
  const found = new Set();
  const probe = { isDir: false, user: { role: '' }, autos: new Proxy({}, { get: (_, k) => { if (typeof k === 'string') found.add(k); return {}; } }),
    canView: (r) => { found.add(r); return false; }, canViewSub: (r) => { found.add(r); return false; } };
  try { s.allow(probe); } catch { /* sonde */ }
  return Array.from(found);
};

export const rubriquesOfScreen = (screenId) => {
  const r = rubriqueOfScreen(screenId);
  return r ? [r] : probeRubriques(screenId);
};

const Check = ({ checked, onChange, testid, title }) => (
  <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} className="h-4 w-4 accent-[#1173D4] cursor-pointer" data-testid={testid} title={title} />
);

/** Sélecteur de périmètre (quels médecins l'employé voit) pour les rubriques qui le supportent. */
const PerimetreCell = ({ u, rub, modes, medecins, pending, setPending }) => {
  const [openList, setOpenList] = useState(false);
  const ex = u.autorisations?.[rub] || {};
  const p = pending[u.id] || {};
  const mode = p.perimetre !== undefined ? p.perimetre : (ex.perimetre || 'defaut');
  const ids = p.perimetre_medecins !== undefined ? p.perimetre_medecins : (ex.perimetre_medecins || []);
  const set = (patch) => setPending(prev => ({ ...prev, [u.id]: { ...(prev[u.id] || {}), ...patch } }));
  const toggle = (mid) => set({ perimetre_medecins: ids.includes(mid) ? ids.filter(x => x !== mid) : [...ids, mid] });
  return (
    <td className="p-1 text-left min-w-[190px]">
      <select value={mode} onChange={e => set({ perimetre: e.target.value })} className="h-7 text-[11px] border rounded px-1 bg-white w-full" data-testid={`rubrique-access-${u.id}-perimetre`}>
        {Object.entries(modes).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {mode === 'liste' && (
        <div className="mt-1">
          <button type="button" onClick={() => setOpenList(o => !o)} className="text-[10px] text-[#1173D4] hover:underline" data-testid={`rubrique-access-${u.id}-perimetre-choisir`}>
            {ids.length ? `${ids.length} médecin(s) choisi(s)` : 'Choisir les médecins…'} {openList ? '▲' : '▼'}
          </button>
          {openList && (
            <div className="mt-1 max-h-32 overflow-y-auto border rounded p-1 bg-white space-y-0.5" data-testid={`rubrique-access-${u.id}-perimetre-liste`}>
              {medecins.map(m => (
                <label key={m.id} className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                  <input type="checkbox" checked={ids.includes(m.id)} onChange={() => toggle(m.id)} className="h-3 w-3 accent-[#1173D4]" data-testid={`rubrique-access-${u.id}-perimetre-med-${m.id}`} />
                  Dr {m.prenom} {m.nom}
                </label>
              ))}
              {!medecins.length && <div className="text-[10px] text-gray-400">Aucun médecin actif</div>}
            </div>
          )}
        </div>
      )}
      {mode === 'mes_medecins' && <div className="text-[10px] text-gray-400 mt-0.5">Suit les assignations (Personnel)</div>}
      {mode === 'defaut' && ['vue_directeur', 'droits_directeur', 'gestion_complete'].some(k => (p[k] !== undefined ? p[k] : ex[k])) && <div className="text-[10px] text-emerald-700 mt-0.5" data-testid={`rubrique-access-${u.id}-perimetre-hint`}>Vue directeur cochée → voit tout</div>}
    </td>
  );
};

/** Matrice employés × (drapeaux + fonctionnalités) pour UNE rubrique. */
const DroitsMatrix = ({ rub, users, catalogue, pending, setPending, onSetDefault, onResetDefault }) => {
  const flags = catalogue.flags;
  const features = catalogue.catalogue[rub]?.features || {};
  const hasPerimetre = !!catalogue.catalogue[rub]?.perimetre;
  const medecins = users.filter(u => u.role === 'Médecin');
  const cols = [...Object.entries(flags).map(([k, v]) => [k, { label: typeof v === 'string' ? v : v.label }]), ...Object.entries(features).map(([k, v]) => [k, { label: v, feature: true }])];
  const valueOf = (u, k) => (pending[u.id]?.[k] !== undefined ? pending[u.id][k] : hasDroit(u, rub, k, { brut: true }));
  const setVal = (uid, k, v) => setPending(p => ({ ...p, [uid]: { ...(p[uid] || {}), [k]: v } }));
  const roles = ROLES_GROUPES.filter(r => users.some(u => roleKey(u.role) === r));
  const ofRole = (r) => users.filter(u => roleKey(u.role) === r);
  const explicitOf = (u) => u.autorisations?.[rub];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="rubrique-access-table">
        <thead>
          <tr className="text-left text-[10px] text-gray-500 border-b align-bottom">
            <th className="p-1.5 min-w-[180px] sticky left-0 bg-white z-10">Employé / groupe</th>
            {cols.map(([k, c]) => (
              <th key={k} className={`p-1 text-center min-w-[64px] max-w-[96px] ${c.feature ? 'text-violet-700' : ''}`} title={c.label} data-testid={`rubrique-access-col-${k}`}>
                <div className="leading-tight line-clamp-3 break-words">{c.label}</div>
              </th>
            ))}
            {hasPerimetre && <th className="p-1 text-left min-w-[190px] text-emerald-700" title="Quels médecins cet employé voit dans À traiter" data-testid="rubrique-access-col-perimetre">Périmètre (médecins vus)</th>}
            <th className="p-1 text-center min-w-[70px]">Défaut</th>
          </tr>
        </thead>
        <tbody>
          {roles.map(role => (
            <tr key={`role-${role}`} className="bg-blue-50/70 border-b" data-testid={`rubrique-access-role-${role}`}>
              <td className="p-1.5 font-semibold text-[#1173D4] sticky left-0 bg-blue-50 z-10"><Users className="inline h-3.5 w-3.5 mr-1" />Tous les {role}s <span className="text-[10px] text-gray-400 font-normal">({ofRole(role).length})</span></td>
              {cols.map(([k]) => (
                <td key={k} className="p-1 text-center">
                  <Check checked={ofRole(role).length > 0 && ofRole(role).every(u => valueOf(u, k))} onChange={v => ofRole(role).forEach(u => setVal(u.id, k, v))} testid={`rubrique-access-role-${role}-${k}`} />
                </td>
              ))}
              {hasPerimetre && <td className="p-1 text-[10px] text-gray-400">— par employé —</td>}
              <td className="p-1 text-center">
                {(catalogue.custom_defaults?.[role] || []).includes(rub) ? (
                  <button type="button" className="text-[10px] text-amber-700 font-semibold hover:underline inline-flex items-center gap-0.5 whitespace-nowrap" title="Défaut personnalisé actif pour ce rôle — cliquer pour le désactiver (retour au défaut standard)"
                    onClick={() => onResetDefault?.(role)} data-testid={`rubrique-access-role-${role}-default`}>
                    <Star className="h-3 w-3 fill-amber-400 text-amber-500" /> défaut actif
                  </button>
                ) : (
                  <button type="button" className="text-[10px] text-amber-600 hover:underline inline-flex items-center gap-0.5" title="Enregistrer les cases de cette ligne comme défaut du rôle (appliqué aux employés sans réglage explicite et aux nouveaux)"
                    onClick={() => onSetDefault(role, Object.fromEntries(cols.map(([k]) => [k, ofRole(role).length > 0 && ofRole(role).every(u => valueOf(u, k))])))} data-testid={`rubrique-access-role-${role}-default`}>
                    <Star className="h-3 w-3" /> défaut
                  </button>
                )}
              </td>
            </tr>
          ))}
          {users.map(u => {
            const ex = explicitOf(u); const touched = !!pending[u.id];
            const isDefault = !(ex && Object.keys(ex).length) && !touched;
            return (
              <tr key={u.id} className={`border-b ${isDefault ? 'text-gray-400' : ''}`} data-testid={`rubrique-access-user-${u.id}`}>
                <td className="p-1.5 sticky left-0 bg-white z-10">{u.prenom} {u.nom} <span className="text-[9px] uppercase text-gray-400 ml-1">{u.role}</span>{isDefault && <span className="text-[9px] ml-1">(défaut)</span>}</td>
                {cols.map(([k]) => (
                  <td key={k} className="p-1 text-center"><Check checked={valueOf(u, k)} onChange={v => setVal(u.id, k, v)} testid={`rubrique-access-${u.id}-${k}`} /></td>
                ))}
                {hasPerimetre && <PerimetreCell u={u} rub={rub} modes={catalogue.perimetre_modes || {}} medecins={medecins} pending={pending} setPending={setPending} />}
                <td className="p-1 text-center">
                  {(ex && Object.keys(ex).length > 0) && (
                    <button type="button" className="text-[10px] text-gray-400 hover:text-red-500 inline-flex items-center gap-0.5" title="Retirer les réglages explicites → revenir au défaut du rôle"
                      onClick={() => setPending(p => ({ ...p, [u.id]: { __reset: true } }))} data-testid={`rubrique-access-${u.id}-reset`}><RotateCcw className="h-3 w-3" /> défaut</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export { DroitsMatrix, ROLES_GROUPES, roleKey, API };

/** Bouton « Accès » (directeur) + dialog : qui peut voir / modifier / utiliser chaque fonctionnalité de l'écran courant. */
export const RubriqueAccess = ({ user, screenId, rubriques: rubriquesProp, label, triggerLabel, triggerClassName, onSaved }) => {
  const { ready, catalogue } = useDroits(user);
  const rubriques = useMemo(() => {
    const base = rubriquesProp || rubriquesOfScreen(screenId);
    const cat = catalogue?.catalogue || {};
    const parent = base[0] && cat[base[0]]?.parent;
    const enfants = base[0] ? Object.keys(cat).filter(k => cat[k].parent === base[0]) : [];
    return [...new Set([...base, ...enfants, ...(RUBRIQUES_LIEES[base[0]] || []), ...(parent ? [parent] : [])])].filter(r => cat[r] || !catalogue);
  }, [rubriquesProp, screenId, catalogue]);
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState(null);
  const [rub, setRub] = useState(rubriques[0]);
  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setRub(rubriques[0]); }, [rubriques]);
  useEffect(() => {
    if (!open || users) return;
    axios.get(`${API}/users`).then(r => setUsers((r.data || []).filter(u => u.role !== 'Directeur' && u.actif !== false))).catch(() => toast.error('Erreur chargement des employés'));
  }, [open, users]);

  if (!estDirecteur(user, 'admin') || !ready || !rubriques.length) return null;

  const save = async () => {
    const ids = Object.keys(pending);
    if (!ids.length) { setOpen(false); return; }
    setSaving(true);
    try {
      for (const uid of ids) {
        const p = pending[uid];
        const valeurs = p.__reset ? Object.fromEntries([...Object.keys(catalogue.flags), ...Object.keys(catalogue.catalogue[rub]?.features || {}), 'perimetre', 'perimetre_medecins'].map(k => [k, null])) : p;
        await axios.put(`${API}/droits/utilisateur/${uid}`, { rubrique: rub, valeurs });
      }
      toast.success(`Accès « ${catalogue.catalogue[rub]?.label || rub} » mis à jour pour ${ids.length} employé(s)`);
      setPending({}); setUsers(null); setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur enregistrement des accès'); }
    finally { setSaving(false); }
  };

  const setDefault = async (role, valeurs) => {
    try {
      await axios.put(`${API}/droits/defaults/${encodeURIComponent(role)}`, { rubrique: rub, valeurs });
      await invalidateDroits();
      toast.success(`Défaut « ${catalogue.catalogue[rub]?.label || rub} » activé pour les ${role}s`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur enregistrement du défaut'); }
  };
  const resetDefault = async (role) => {
    try {
      await axios.delete(`${API}/droits/defaults/${encodeURIComponent(role)}/${rub}`);
      await invalidateDroits();
      toast.success(`Défaut personnalisé « ${catalogue.catalogue[rub]?.label || rub} » désactivé pour les ${role}s — retour au défaut standard`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const title = label || SCREENS[screenId]?.label || catalogue.catalogue[rub]?.label || rub;
  return (
    <>
      <Button size="sm" variant="outline" className={triggerClassName || "h-7 text-xs gap-1 border-dashed"} onClick={() => setOpen(true)} title="Qui peut voir / modifier / utiliser cette rubrique" data-testid="rubrique-access-btn">
        <ShieldCheck className="h-3.5 w-3.5" /> {triggerLabel || 'Accès'}
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!o) setPending({}); setOpen(o); }}>
        <DialogContent className="max-w-[min(96vw,1400px)] max-h-[92vh] overflow-y-auto" data-testid="rubrique-access-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#1173D4]" /> Accès — {title}</DialogTitle>
            <DialogDescription>
              Cochez par employé ou par groupe : <b>Voir</b> (accès à la rubrique), <b>Modifier</b>, <b>Vue directeur</b> (toutes les données / tous les médecins), <b>Actions directeur</b>, puis les <span className="text-violet-700 font-medium">fonctionnalités spécifiques</span> de l'écran{catalogue.catalogue[rub]?.perimetre ? <> et le <span className="text-emerald-700 font-medium">périmètre</span> (quels médecins l'employé voit : tous, ses médecins assignés ou une liste choisie)</> : null}. Lignes grisées = défaut du rôle. ★ défaut = enregistrer la ligne du groupe comme défaut du rôle (employés sans réglage explicite + nouveaux) ; « défaut actif » = cliquer pour le désactiver.
            </DialogDescription>
          </DialogHeader>
          {rubriques.length > 1 && (
            <div className="flex flex-wrap gap-1" data-testid="rubrique-access-tabs">
              {rubriques.map(r => <button key={r} onClick={() => { setRub(r); setPending({}); }} className={`px-2 py-1 rounded text-xs border ${r === rub ? 'bg-[#1173D4] text-white border-[#1173D4]' : 'text-gray-600 hover:bg-gray-50'}`} data-testid={`rubrique-access-tab-${r}`}>{catalogue.catalogue[r]?.label || r}{catalogue.catalogue[r]?.parent === undefined ? '' : ''}</button>)}
            </div>
          )}
          {!users ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#1173D4]" /></div>
            : <DroitsMatrix rub={rub} users={users} catalogue={catalogue} pending={pending} setPending={setPending} onSetDefault={setDefault} onResetDefault={resetDefault} />}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setPending({}); setOpen(false); }} data-testid="rubrique-access-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving || !Object.keys(pending).length} data-testid="rubrique-access-save">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
